import React, { useState, useRef, useEffect, useMemo } from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";
import { Input } from "./input2";

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: string;
  disabled?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  className,
  error,
  disabled
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Filter options based on user input (value)
  // If the value exactly matches an option, we still show other options that match the prefix?
  // Usually autocomplete shows matches containing the string.
  const filteredOptions = useMemo(() => {
    if (!value) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(value.toLowerCase())
    );
  }, [options, value]);

  // Handle clicking outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        // Optional: Ensure strict selection on blur?
        // If we want to strictly enforce selection, check if 'value' matches any option value.
        // If not, clear it.
        // const isValid = options.some(opt => opt.value === value);
        // if (!isValid && value !== "") {
        //   onChange(""); 
        // }
        // For now, focusing on the UI behavior requested (write -> dropdown).
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [wrapperRef, options, value, onChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    setIsOpen(true);
    setHighlightedIndex(0);
  };

  const handleSelectOption = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" && isOpen && filteredOptions.length > 0) {
      e.preventDefault();
      handleSelectOption(filteredOptions[highlightedIndex].value);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  return (
    <div className={cn("relative w-full", className)} ref={wrapperRef}>
      <div className="relative">
        <Input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => !disabled && setIsOpen(true)}
          placeholder={placeholder}
          onClick={() => !disabled && setIsOpen(true)}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          className={cn(
            "pr-10",
            error ? "border-red-500 focus:border-red-500 focus:ring-red-200" : ""
          )}
          autoComplete="off"
        />
        {/* Helper icon to indicate it's a dropdown */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <ChevronDown className="h-4 w-4 text-gray-400 opacity-50" />
        </div>
      </div>

      {isOpen && (filteredOptions.length > 0 || value) && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              No matching address found.
            </div>
          ) : (
            <div className="py-1">
              {filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  className={cn(
                    "px-4 py-2 text-sm cursor-pointer flex items-center justify-between",
                    index === highlightedIndex ? "bg-gray-100 text-gray-900" : "text-gray-700 hover:bg-gray-50",
                    value === option.value && "bg-purple-50 text-[#885ABB]"
                  )}
                  onMouseDown={(e) => {
                    // Prevent input blur before click is registered
                    e.preventDefault();
                  }}
                  onClick={() => handleSelectOption(option.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <span className="truncate">{option.label}</span>
                  {value === option.value && (
                    <Check className="h-4 w-4 text-[#885ABB]" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
